// Meta layer: Memory Web purchases, new runs, Molt and the Reliquary (docs/04 §19–20).
import { B } from '../content/balance';
import { CHARM_LIST, CHARMS, NODES, RELIC_LIST, RELICS, SYMBOL_LIST, WEB_ADJ } from '../content/registry';
import { D } from './decimal';
import { say, sayScript, tip } from './dialogue';
import { newInstance } from './grid';
import { newMood } from './hunger';
import { computeModifiers, metaModifiers, type Modifiers } from './modifiers';
import { newReckoningTrack } from './reckoning';
import { createStreams, stream } from './rng';
import { soulsForTribute, tribute } from './tribute';
import type { FormId, GameEvent, GameState, HandSettings, KeyAction, MetaState, RunState, Settings, SizeId } from './types';
import { startWindow } from './window';

export const SAVE_VERSION = 6;

export function defaultSettings(): Settings {
  return {
    master: 0.8,
    music: 0.6,
    sfx: 0.8,
    speed: 1,
    shake: 1,
    reduceFlashing: false,
    crt: true,
    notation: 'suffix',
    muscleMemory: false,
    tips: true,
    keys: defaultKeys(),
    repeatMs: 700,
  };
}

/** docs/07 §5: Space pull, P pay, H hand, Enter collect, G gamble, 1–5 hold, QWERT nudge ▲, ASDFG nudge ▼. */
export function defaultKeys(): Record<KeyAction, string> {
  const k = {
    pull: ' ',
    pay: 'p',
    hand: 'h',
    collect: 'enter',
    gamble: 'g',
  } as Record<KeyAction, string>;
  ['q', 'w', 'e', 'r', 't'].forEach((c, i) => (k[`up${i + 1}` as KeyAction] = c));
  ['a', 's', 'd', 'f', 'g'].forEach((c, i) => (k[`down${i + 1}` as KeyAction] = c));
  for (let i = 1; i <= 5; i++) k[`hold${i}` as KeyAction] = String(i);
  return k;
}

export function defaultHand(): HandSettings {
  return {
    autospin: false,
    payAt: B.HAND_PAY_AT,
    feedAt: B.HAND_FEED_AT,
    stopOnBig: true,
    stopOnBonus: true,
    declineGambles: false,
    stopLowSpins: 0,
    draftPriority: [],
  };
}

export function createMeta(seed: string): MetaState {
  return {
    seed,
    runCounter: 0,
    souls: 0,
    nodes: { ROOT: 1 },
    lifetime: {
      consumed: 0,
      maxTribute: 0,
      soulsEarned: 0,
      spins: 0,
      perfectPulls: 0,
      bonuses: {},
      dealsAccepted: 0,
      dealsRefused: 0,
      reckoningsBeaten: 0,
      collectorsBeaten: [],
      ledgerPages: [],
      plugParts: [],
      endings: [],
      teethEarned: 0,
      fed: {},
      bestEndless: 0,
      devours: 0,
      miracles: 0,
      counters: {},
    },
    flags: {},
    tipsSeen: [],
    recentLines: [],
    almanac: { symbols: [], charms: [], relics: [], collectors: [] },
    settings: defaultSettings(),
    lastRun: null,
    teeth: 0,
    molts: 0,
    form: 'brass',
    cycleMaxTribute: 0,
    relics: { owned: [], slotted: Array.from({ length: B.RELIC_SLOTS_BASE }, () => null), shards: 0 },
    hand: defaultHand(),
    runSize: null,
    history: [],
    achievements: [],
  };
}

export function createInitialState(seed: string): GameState {
  return { version: SAVE_VERSION, meta: createMeta(seed), run: null };
}

// ── Memory Web ────────────────────────────────────────────────────────

export function nodeLevel(meta: MetaState, id: string): number {
  return meta.nodes[id] ?? 0;
}

export function nodeCost(id: string, currentLevel: number): number {
  const def = NODES.get(id);
  if (!def) return Infinity;
  return Math.ceil(def.baseCost * Math.pow(B.NODE_GROWTH, currentLevel));
}

export type NodeStatus = 'owned' | 'maxed' | 'buyable' | 'affordable' | 'locked' | 'hidden';

export function nodeStatus(meta: MetaState, id: string): { status: NodeStatus; cost: number; missing: string[]; needTribute?: number } {
  const def = NODES.get(id);
  if (!def) return { status: 'hidden', cost: Infinity, missing: [] };
  const level = nodeLevel(meta, id);
  const cost = nodeCost(id, level);
  if (level >= def.maxLevel) return { status: 'maxed', cost, missing: [] };
  const adjacent = [...(WEB_ADJ.get(id) ?? [])].some((n) => nodeLevel(meta, n) > 0);
  const missing = (def.requiresAll ?? []).filter((r) => nodeLevel(meta, r) <= 0);
  if (!adjacent && level === 0) return { status: 'hidden', cost, missing };
  if (missing.length) return { status: 'locked', cost, missing };
  // "Reaching" a tribute counts the window you died in, like the Molt itself (cycleMaxTribute).
  if (def.requiresMaxTribute && Math.max(meta.lifetime.maxTribute, meta.cycleMaxTribute) < def.requiresMaxTribute)
    return { status: 'locked', cost, missing: [], needTribute: def.requiresMaxTribute };
  return { status: meta.souls >= cost ? 'affordable' : 'buyable', cost, missing };
}

export function buyNode(state: GameState, id: string, events: GameEvent[]): string | undefined {
  if (state.run && state.run.phase !== 'consumed') return 'finish the run first';
  const { status, cost } = nodeStatus(state.meta, id);
  if (status !== 'affordable') return `cannot buy (${status})`;
  state.meta.souls -= cost;
  state.meta.nodes[id] = nodeLevel(state.meta, id) + 1;
  events.push({ e: 'NODE_BOUGHT', id, level: state.meta.nodes[id]!, cost });
  return undefined;
}

// ── Runs ──────────────────────────────────────────────────────────────

const SIZE_ORDER: SizeId[] = ['bandit', 'cabinet', 'wide', 'grand', 'tall', 'cathedral'];

/** The run's machine size: the grown size, or a smaller one the player chose (docs/04 §1.1). */
export function runSize(meta: MetaState, mods: Modifiers, wanted?: SizeId | null): SizeId {
  const want = wanted ?? meta.runSize;
  if (!want) return mods.size;
  return SIZE_ORDER.indexOf(want) <= SIZE_ORDER.indexOf(mods.size) ? want : mods.size;
}

export function createRun(meta: MetaState, mods: Modifiers, size: SizeId = mods.size): RunState {
  const seed = `${meta.seed}#${meta.runCounter}`;
  const run: RunState = {
    seed,
    rng: createStreams(seed),
    size,
    deck: [],
    nextUid: 1,
    strips: [],
    stops: [],
    held: [],
    freeHold: [],
    window: { n: 0, tribute: D(0), spinsLeft: 0, spinsTotal: 0, spinsUsed: 0, counters: {} },
    coins: D(0),
    soulsPending: 0,
    charges: { hold: 0, nudge: 0 },
    nextWindow: { spins: 0, hold: 0, nudge: 0 },
    luckTemp: 0,
    deadStreak: 0,
    spinIndex: 0,
    hunger: 0,
    mood: newMood(),
    eaten: [],
    holdPaid: [],
    charms: [],
    charmBank: {},
    hymns: {},
    freeCharms: 0,
    phase: 'ready',
    spin: null,
    intermission: null,
    bonus: null,
    stats: { spins: 0, wins: 0, bestWin: D(0), perfectPulls: 0, nudgesUsed: 0, holdsUsed: 0, tributesPaid: 0, destroyed: 0, bonuses: 0 },
    reck: newReckoningTrack(),
    hexes: [],
    doom: 0,
    deals: [],
    nightShift: null,
    gamble: null,
    flags: {},
  };
  for (const [defId, count] of B.START_DECK) for (let i = 0; i < count; i++) run.deck.push(newInstance(run, defId));
  return run;
}

/** Starting tribute: Head Start and the Deed stack, up to START_TRIBUTE_MAX. */
export function startTributeOf(mods: Modifiers): number {
  const head = Math.max(1, mods.startTribute);
  if (!mods.startTributeAdd) return head;
  return Math.min(B.START_TRIBUTE_MAX, (head > 1 ? head : 0) + mods.startTributeAdd);
}

export function newRun(state: GameState, events: GameEvent[], wanted?: SizeId): string | undefined {
  if (state.run && state.run.phase !== 'consumed' && state.run.phase !== 'remember') return 'a run is active';
  const meta = state.meta;
  const mods = metaModifiers(meta);
  const run = createRun(meta, mods, runSize(meta, mods, wanted));
  meta.runCounter++;
  const kept = state.run?.flags.locketCharm !== undefined ? state.run.charms[state.run.flags.locketCharm] : undefined;
  state.run = run;

  if (mods.startCoinsT1 > 0) run.coins = tribute(1, mods).mul(mods.startCoinsT1);
  const startN = startTributeOf(mods);
  for (let n = 1; n < startN; n++) run.soulsPending += soulsForTribute(n, mods);
  const shop = stream(run.rng, 'shop');
  if (kept && CHARMS.has(kept)) run.charms.push(kept);
  if (mods.startCommonCharm) {
    const pool = CHARM_LIST.filter((c) => c.rarity === 'C' && !c.cursed && !run.charms.includes(c.id) && (!c.requires || c.requires(mods)));
    if (pool.length) run.charms.push(shop.pick(pool).id);
  }
  // Glass Eye: a random Rare symbol joins the starting deck.
  for (let i = 0; i < mods.startRare; i++) {
    const pool = SYMBOL_LIST.filter((d) => d.rarity === 'R' && !d.sigil && !d.noDraft && mods.symbolGates.has(d.gate));
    if (pool.length) run.deck.push(newInstance(run, shop.pick(pool).id));
  }

  events.push({ e: 'RUN_START', size: run.size, n: startN, seed: run.seed });
  if (mods.features.has('face') && !meta.flags.awake) {
    meta.flags.awake = true;
    events.push({ e: 'IT_WAKES' });
    say(state, 'wake', events, undefined, { force: true, awake: true });
  } else {
    const grown = `grown_${run.size}`;
    const prev = SIZE_ORDER[SIZE_ORDER.indexOf(run.size) - 1];
    if (run.size === mods.size && SIZE_ORDER.indexOf(run.size) >= 2 && !meta.flags[grown] && prev) {
      meta.flags[grown] = true;
      events.push({ e: 'MACHINE_GROWS', from: prev, to: run.size });
      // Act III: the tarps fall (docs/06 §4); Act IV: the Cathedral set piece.
      if (run.size === 'tall') sayScript(state, 'siblings', events, 'noticed');
      else if (run.size === 'cathedral') sayScript(state, 'cathedral', events, 'adoring');
      else say(state, 'grow', events, undefined, { force: true });
    }
  }
  if (!mods.features.has('face') && meta.lifetime.consumed >= 3 && !meta.flags.hungryPrinted) {
    meta.flags.hungryPrinted = true;
    say(state, 'hungry', events, undefined, { force: true, awake: false });
  }
  tip(state, 'lever', events);
  startWindow(state, computeModifiers(meta, run), startN, events);
  tip(state, 'receipt', events);
  return undefined;
}

// ── Molt (docs/04 §20) ────────────────────────────────────────────────

export function teethForMolt(meta: MetaState, mods: Pick<Modifiers, 'features'> = metaModifiers(meta)): number {
  if (!mods.features.has('molt')) return 0;
  const over = meta.cycleMaxTribute - B.TEETH_OFFSET;
  return over > 0 ? Math.floor(Math.pow(over, B.TEETH_EXP)) : 0;
}

export function canMolt(meta: MetaState): boolean {
  return metaModifiers(meta).features.has('molt') && meta.cycleMaxTribute >= B.MOLT_MIN_TRIBUTE;
}

export function molt(state: GameState, events: GameEvent[]): string | undefined {
  if (state.run && state.run.phase !== 'consumed') return 'finish the run first';
  const meta = state.meta;
  if (!canMolt(meta)) return 'cannot molt yet';
  const teeth = teethForMolt(meta);
  meta.teeth += teeth;
  meta.lifetime.teethEarned += teeth;
  meta.souls = 0;
  for (const [id, level] of Object.entries(meta.nodes)) if (level > 0 && NODES.get(id)?.kind === 'stat') delete meta.nodes[id];
  meta.molts++;
  const k = Math.min(B.FORMS.length - 1, B.FORMS.indexOf(meta.form) + 1);
  meta.form = B.FORMS[k] as FormId;
  meta.cycleMaxTribute = 0;
  events.push({ e: 'MOLTED', teeth, form: meta.form, molts: meta.molts });
  if (state.run) sayScript(state, 'molt', events, 'glitch', { n: meta.lifetime.consumed });
  return undefined;
}

// ── Reliquary ─────────────────────────────────────────────────────────

export function relicAvailable(meta: MetaState, id: string): boolean {
  const def = RELICS.get(id);
  return !!def && !meta.relics.owned.includes(id) && (!def.requires || def.requires(meta));
}

export function buyRelic(state: GameState, id: string, events: GameEvent[]): string | undefined {
  const meta = state.meta;
  const def = RELICS.get(id);
  if (!def || !relicAvailable(meta, id)) return 'not available';
  if (meta.teeth < def.cost) return 'not enough teeth';
  meta.teeth -= def.cost;
  gainRelic(state, id, 'teeth', events);
  return undefined;
}

export function gainRelic(state: GameState, id: string, by: string, events: GameEvent[]): void {
  const meta = state.meta;
  if (meta.relics.owned.includes(id)) return;
  meta.relics.owned.push(id);
  if (!meta.almanac.relics.includes(id)) meta.almanac.relics.push(id);
  const free = meta.relics.slotted.indexOf(null);
  if (free >= 0) meta.relics.slotted[free] = id;
  events.push({ e: 'RELIC_GAINED', id, by });
}

/** Relic Shards: every SHARDS_PER_RELIC become a random relic you don't own. */
export function addShard(state: GameState, rngPick: (ids: string[]) => string, events: GameEvent[]): void {
  const meta = state.meta;
  meta.relics.shards++;
  events.push({ e: 'SHARD_GAINED', shards: meta.relics.shards });
  if (meta.relics.shards < B.SHARDS_PER_RELIC) return;
  const pool = RELIC_LIST.filter((r) => relicAvailable(meta, r.id)).map((r) => r.id);
  if (!pool.length) return;
  meta.relics.shards -= B.SHARDS_PER_RELIC;
  gainRelic(state, rngPick(pool), 'shards', events);
}

export function slotRelic(state: GameState, id: string | null, slot: number): string | undefined {
  if (state.run && state.run.phase !== 'consumed') return 'between runs only';
  const r = state.meta.relics;
  if (slot < 0 || slot >= r.slotted.length) return 'bad slot';
  if (id !== null && !r.owned.includes(id)) return 'not owned';
  if (id !== null) {
    const at = r.slotted.indexOf(id);
    if (at >= 0) r.slotted[at] = r.slotted[slot] ?? null;
  }
  r.slotted[slot] = id;
  return undefined;
}

export function relicSlotCost(meta: MetaState): number | null {
  const extra = meta.relics.slotted.length - B.RELIC_SLOTS_BASE;
  return B.RELIC_SLOT_COSTS[extra] ?? null;
}

export function buyRelicSlot(state: GameState): string | undefined {
  const meta = state.meta;
  const cost = relicSlotCost(meta);
  if (cost === null) return 'max slots';
  if (meta.teeth < cost) return 'not enough teeth';
  meta.teeth -= cost;
  meta.relics.slotted.push(null);
  return undefined;
}
